import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Clock, CheckCircle2, Smartphone, Calendar, MapPin, Shirt, Briefcase, Users } from "lucide-react";
import { SchemaMarkup, generateBreadcrumbSchema, generateHowToSchema } from "@/components/SchemaMarkup";
import { CURRENT_YEAR } from "@/lib/currentYear";

const MinimizeWaitTimes = () => {
  const tips = [
    {
      number: 1,
      title: "Get TSA PreCheck or CLEAR",
      description: "TSA PreCheck ($78 for 5 years) gives you access to dedicated lanes with shorter waits. You keep shoes, belt, and light jacket on. CLEAR ($189/year) uses biometrics to skip ID verification entirely. Combined, they can save 30+ minutes.",
      icon: CheckCircle2,
      savings: "Save 15-30 minutes"
    },
    {
      number: 2,
      title: "Check Real-Time Wait Times Before You Leave",
      description: "Use AirportBuddy to check current TSA wait times at your airport. Knowing if security is running 5 minutes or 45 minutes helps you time your arrival perfectly and avoid unnecessary stress.",
      icon: Smartphone,
      savings: "Save 20-40 minutes"
    },
    {
      number: 3,
      title: "Fly During Off-Peak Hours",
      description: "Avoid the rush hours: 5-8 AM (business travelers), 11 AM-1 PM (lunch rush), and 4-7 PM (evening departures). Midweek flights (Tuesday-Thursday) typically have the shortest lines.",
      icon: Calendar,
      savings: "Save 10-25 minutes"
    },
    {
      number: 4,
      title: "Choose the Right Security Lane",
      description: "Most right-handed people instinctively turn right—so go left. Check for multiple security checkpoints; smaller ones in different terminals are often less crowded than the main checkpoint.",
      icon: MapPin,
      savings: "Save 5-15 minutes"
    },
    {
      number: 5,
      title: "Prepare Before You Reach the Scanner",
      description: "Empty pockets, remove belt and watch, have laptop and liquids accessible before you're at the bin. Wear slip-on shoes and avoid jewelry. Being prepared shaves minutes off your screening time.",
      icon: Shirt,
      savings: "Save 3-5 minutes"
    }
  ];

  const bonusTips = [
    { icon: Briefcase, tip: "Use a TSA-approved laptop bag that opens flat for faster scanning" },
    { icon: Users, tip: "If traveling with kids, use family lanes where available" },
    { icon: Clock, tip: "Arrive 2 hours early for domestic, 3 hours for international flights" }
  ];

  const howToSchema = generateHowToSchema(
    "Top 5 Tips for Minimizing Airport Wait Times",
    "Learn the top 5 proven strategies to minimize your airport security wait times and breeze through TSA checkpoints faster.",
    tips.map(tip => ({
      name: tip.title,
      text: tip.description
    })),
    "PT45M"
  );

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the best way to minimize airport wait times?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best way to minimize airport wait times is to get TSA PreCheck, check real-time wait times before arriving, fly during off-peak hours, choose less crowded security lanes, and prepare your items before reaching the scanner."
        }
      },
      {
        "@type": "Question",
        "name": "How much time can TSA PreCheck save?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TSA PreCheck typically saves 15-30 minutes at security checkpoints. PreCheck lanes are dedicated and shorter, plus you don't need to remove shoes, belt, or laptop."
        }
      },
      {
        "@type": "Question",
        "name": "What are the best times to fly to avoid long security lines?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best times to fly are mid-morning (9-11 AM) and early afternoon (1-4 PM) on Tuesdays, Wednesdays, or Thursdays. Avoid early morning (5-8 AM), lunch rush (11 AM-1 PM), and evening (4-7 PM) on Mondays, Fridays, and Sundays."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{`5 Tips to Skip Airport Security Lines [${CURRENT_YEAR}] – Save 30+ Min`}</title>
        <meta name="description" content="Skip long TSA lines with these 5 proven tips. ⏱️ Save 30+ minutes at airport security. TSA PreCheck, real-time wait times & more." />
        <meta name="keywords" content="minimize airport wait times, reduce TSA wait, airport security tips, fastest through airport security, TSA PreCheck benefits, airport travel hacks" />
        <link rel="canonical" href="https://airportbuddy.app/tips/minimize-wait-times" />
        
        {/* Freshness signals */}
        <meta property="article:published_time" content="2025-01-01" />
        <meta property="article:modified_time" content="2026-01-30" />
        <meta property="og:updated_time" content="2026-01-30" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://airportbuddy.app/tips/minimize-wait-times" />
        <meta property="og:title" content="5 Tips to Skip Airport Security Lines – Save 30+ Minutes" />
        <meta property="og:description" content="Learn the top 5 proven tips to minimize airport security wait times." />
        <meta property="og:image" content="https://airportbuddy.app/og-image.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`5 Tips to Skip Airport Security Lines [${CURRENT_YEAR}]`} />
        <meta name="twitter:description" content="Save 30+ minutes at airport security with these 5 proven tips." />
      </Helmet>

      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Tips", url: "/tips" },
        { name: "Minimize Wait Times", url: "/tips/minimize-wait-times" }
      ])} />
      <SchemaMarkup schema={howToSchema} />
      <SchemaMarkup schema={faqSchema} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </nav>

          {/* Hero Section */}
          <header className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Clock className="h-4 w-4" />
              Updated for {CURRENT_YEAR}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Top 5 Tips for Minimizing Airport Wait Times
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Skip the stress and save 30+ minutes at security with these proven strategies used by frequent flyers.
            </p>
          </header>

          {/* Main Tips */}
          <div className="max-w-4xl mx-auto space-y-6 mb-12">
            {tips.map((tip) => {
              const IconComponent = tip.icon;
              return (
                <Card key={tip.number} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary text-primary-foreground p-6 flex items-center justify-center md:w-24">
                        <span className="text-4xl font-bold">{tip.number}</span>
                      </div>
                      <div className="p-6 flex-1">
                        <div className="flex items-start gap-4">
                          <div className="bg-primary/10 p-3 rounded-full shrink-0">
                            <IconComponent className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3 mb-2">
                              <h2 className="text-xl font-semibold text-foreground">{tip.title}</h2>
                              <span className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs font-medium px-2.5 py-1 rounded-full">
                                {tip.savings}
                              </span>
                            </div>
                            <p className="text-muted-foreground leading-relaxed">{tip.description}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bonus Tips */}
          <div className="max-w-4xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">Bonus Quick Tips</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {bonusTips.map((bonus, index) => {
                const IconComponent = bonus.icon;
                return (
                  <Card key={index} className="text-center p-6">
                    <div className="bg-accent/20 p-3 rounded-full w-fit mx-auto mb-3">
                      <IconComponent className="h-6 w-6 text-accent-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">{bonus.tip}</p>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-primary text-primary-foreground p-8 text-center">
              <h2 className="text-2xl font-bold mb-3">Check Wait Times Right Now</h2>
              <p className="text-primary-foreground/80 mb-6 max-w-xl mx-auto">
                Put Tip #2 into action. See real-time TSA wait times for 100+ airports and know exactly when to leave for your flight.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" variant="secondary">
                  <Link to="/">Check Current Wait Times</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                  <Link to="/blog/tips-for-avoiding-long-lines-at-airport-security">Read Full Security Guide</Link>
                </Button>
              </div>
            </Card>
          </div>

          {/* Related Content */}
          <div className="max-w-4xl mx-auto mt-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Popular Airport Guides</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { code: "ATL", name: "Atlanta Hartsfield-Jackson" },
                { code: "LAX", name: "Los Angeles International" },
                { code: "ORD", name: "Chicago O'Hare" }
              ].map((airport) => (
                <Link 
                  key={airport.code} 
                  to={`/airports/${airport.code.toLowerCase()}/guide`}
                  className="block"
                >
                  <Card className="p-4 hover:shadow-md transition-shadow hover:border-primary">
                    <p className="font-semibold text-foreground">{airport.code}</p>
                    <p className="text-sm text-muted-foreground">{airport.name}</p>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MinimizeWaitTimes;
